import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { ACTIVITY_TIME_CONFIG } from "@/constants/activityTime";
import type { UserSegment } from "@/views/power/components/VipPackageActivity/types";

dayjs.extend(utc);
dayjs.extend(timezone);

export const DEBUG_618_ACTIVITY_KEY = "debug618Activity";
export const DEBUG_618_SEGMENT_KEY = "debug618Segment";

const DEBUG_SEGMENTS: UserSegment[] = ["neverPaid", "expired", "activeVipSvip", "activeUltra"];

const NON_PRODUCTION_ENVS = new Set(["development", "test", "gray"]);

function isNonProductionEnv(): boolean {
  return NON_PRODUCTION_ENVS.has(import.meta.env.VITE_ENV);
}

/**
 * 非生产环境下强制视为 618 活动期内（绕过后台 getActivityRights 未下发）。
 * 开启方式：`.env` 设置 `VITE_DEBUG_618_ACTIVITY=true`，或控制台 `__618Debug.enable()`。
 */
export function isActivity618DebugEnabled(): boolean {
  if (!isNonProductionEnv()) return false;
  if (import.meta.env.VITE_DEBUG_618_ACTIVITY === "true") return true;
  return localStorage.getItem(DEBUG_618_ACTIVITY_KEY) === "1";
}

/** 调试用户分层，用于切换 promo / renewal Hero 与定价 */
export function getActivity618DebugSegment(): UserSegment | null {
  if (!isActivity618DebugEnabled()) return null;
  const raw = localStorage.getItem(DEBUG_618_SEGMENT_KEY);
  if (!raw || !DEBUG_SEGMENTS.includes(raw as UserSegment)) return null;
  return raw as UserSegment;
}

/** 调试模式下的活动剩余秒数（基于 ACTIVITY_TIME_CONFIG.end） */
export function getActivity618DebugRemainingSeconds(serverTimestampMs: number): number {
  const endMs = dayjs.tz(ACTIVITY_TIME_CONFIG.end, "Asia/Shanghai").valueOf();
  const nowMs = serverTimestampMs || Date.now();
  return Math.max(0, Math.floor((endMs - nowMs) / 1000));
}

/** 挂载控制台快捷方法，便于 QA 切换活动态与用户分层 */
export function installActivity618DebugConsole(): void {
  if (!isNonProductionEnv() || typeof window === "undefined") return;

  const win = window as Window & {
    __618Debug?: {
      enable: () => void;
      disable: () => void;
      setSegment: (segment: UserSegment) => void;
      status: () => { enabled: boolean; segment: UserSegment | null };
    };
  };

  win.__618Debug = {
    enable: () => {
      localStorage.setItem(DEBUG_618_ACTIVITY_KEY, "1");
      location.reload();
    },
    disable: () => {
      localStorage.removeItem(DEBUG_618_ACTIVITY_KEY);
      localStorage.removeItem(DEBUG_618_SEGMENT_KEY);
      location.reload();
    },
    setSegment: (segment: UserSegment) => {
      if (!DEBUG_SEGMENTS.includes(segment)) {
        console.warn("[618Debug] invalid segment:", segment, "allowed:", DEBUG_SEGMENTS);
        return;
      }
      localStorage.setItem(DEBUG_618_ACTIVITY_KEY, "1");
      localStorage.setItem(DEBUG_618_SEGMENT_KEY, segment);
      location.reload();
    },
    status: () => ({
      enabled: isActivity618DebugEnabled(),
      segment: getActivity618DebugSegment()
    })
  };
}
